import { useCallback } from "react";
import { status } from "store/types/enum";
import { handleToast } from "utils/toasts";

export function useReferralShareActions(shareUrl: string, code: string) {
  const effectiveShareUrl = useCallback((): string => {
    if (shareUrl) {
      return shareUrl;
    }
    if (code) {
      return `${window.location.origin}/register?ref=${encodeURIComponent(code)}`;
    }
    return "";
  }, [shareUrl, code]);

  const copyLink = useCallback(async () => {
    const toCopy = effectiveShareUrl();
    if (!toCopy) {
      return;
    }
    try {
      await navigator.clipboard.writeText(toCopy);
      handleToast("Link copied to clipboard.", status.SUCCESS);
    } catch {
      handleToast("Could not copy link.", status.ERROR);
    }
  }, [effectiveShareUrl]);

  const shareEmail = useCallback(() => {
    const url = effectiveShareUrl();
    if (!url) {
      return;
    }
    const subject = encodeURIComponent("Join me on RivetAI");
    const body = encodeURIComponent(
      `I've been using RivetAI for my projects. Sign up with my link — when you buy your first project, we both get a bonus project license.\n\n${url}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }, [effectiveShareUrl]);

  const shareLinkedIn = useCallback(() => {
    const url = effectiveShareUrl();
    if (!url) {
      return;
    }
    const encoded = encodeURIComponent(url);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, [effectiveShareUrl]);

  const shareTwitter = useCallback(() => {
    const url = effectiveShareUrl();
    if (!url) {
      return;
    }
    const text = encodeURIComponent(
      `Check out RivetAI for film & TV production — use my link and we both get a bonus when you buy your first project: ${url}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank", "noopener,noreferrer");
  }, [effectiveShareUrl]);

  return { copyLink, shareEmail, shareLinkedIn, shareTwitter };
}
